import { v4 as uuidv4 } from "uuid";
export class OfficeDocumentIdManager {
    constructor(options) {
        this.options = options;
        this.cachedDocumentId = null;
        this.inFlightGetOrCreate = null;
        this.inFlightGet = null;
    }
    async getOrCreateDocumentId() {
        if (this.cachedDocumentId) {
            return this.cachedDocumentId;
        }
        if (this.inFlightGetOrCreate) {
            return this.inFlightGetOrCreate;
        }
        this.inFlightGetOrCreate = this.runWithCustomXml(async (context, customXmlParts) => {
            const entries = await this.loadPartEntries(context, customXmlParts);
            const canonical = this.pickCanonicalEntry(entries);
            if (canonical) {
                await this.cleanupDuplicateParts(context, entries, canonical.normalizedId, canonical.partId);
                this.cachedDocumentId = canonical.normalizedId;
                console.log(`Found existing Carousel ${this.options.label} in Custom XML:`, canonical.rawId);
                return canonical.normalizedId;
            }
            const rawNewId = uuidv4();
            console.log(`No existing Carousel ${this.options.label} found, creating new:`, rawNewId);
            const xml = this.buildXml(rawNewId);
            const newPart = customXmlParts.add(xml);
            newPart.load("id");
            await context.sync();
            const normalized = this.options.normalizeId(rawNewId);
            this.cachedDocumentId = normalized;
            if (entries.length > 0) {
                await this.cleanupDuplicateParts(context, entries, normalized, newPart.id);
            }
            console.log(`Carousel ${this.options.label} saved to Custom XML Part:`, rawNewId);
            console.log("XML Part ID:", newPart.id);
            return normalized;
        }).finally(() => {
            this.inFlightGetOrCreate = null;
        });
        return this.inFlightGetOrCreate;
    }
    async getDocumentId() {
        if (this.cachedDocumentId) {
            return this.cachedDocumentId;
        }
        if (this.inFlightGet) {
            return this.inFlightGet;
        }
        this.inFlightGet = this.runWithCustomXml(async (context, customXmlParts) => {
            const entries = await this.loadPartEntries(context, customXmlParts);
            const canonical = this.pickCanonicalEntry(entries);
            if (!canonical) {
                return null;
            }
            await this.cleanupDuplicateParts(context, entries, canonical.normalizedId, canonical.partId);
            this.cachedDocumentId = canonical.normalizedId;
            return canonical.normalizedId;
        }).finally(() => {
            this.inFlightGet = null;
        });
        return this.inFlightGet;
    }
    async runWithCustomXml(handler) {
        const hostApi = this.options.host === "powerpoint" ? globalThis.PowerPoint : globalThis.Excel;
        if (!hostApi?.run) {
            throw new Error(`Office host runtime is not available for ${this.options.host}.`);
        }
        return hostApi.run(async (context) => {
            const customXmlParts = this.options.host === "powerpoint"
                ? context.presentation.customXmlParts
                : context.workbook.customXmlParts;
            return handler(context, customXmlParts);
        });
    }
    extractIdFromXml(xml) {
        try {
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(xml, "text/xml");
            const idNode = xmlDoc.getElementsByTagName(this.options.idTag)[0];
            const raw = idNode?.textContent?.trim();
            return raw || null;
        }
        catch {
            return null;
        }
    }
    normalizeRawId(rawId) {
        if (!rawId) {
            return null;
        }
        try {
            const normalized = this.options.normalizeId(rawId);
            return normalized || null;
        }
        catch {
            return null;
        }
    }
    async loadPartEntries(context, customXmlParts) {
        const parts = customXmlParts.getByNamespace(this.options.namespace);
        parts.load("items");
        await context.sync();
        const items = Array.isArray(parts.items) ? parts.items : [];
        if (items.length === 0) {
            return [];
        }
        const xmlResults = [];
        for (const part of items) {
            part.load("id");
            xmlResults.push({
                part,
                xmlResult: part.getXml(),
            });
        }
        await context.sync();
        const entries = [];
        for (const { part, xmlResult } of xmlResults) {
            const rawId = this.extractIdFromXml(xmlResult?.value || "");
            const normalizedId = this.normalizeRawId(rawId);
            entries.push({
                part,
                partId: String(part.id || ""),
                rawId,
                normalizedId,
            });
        }
        return entries;
    }
    pickCanonicalEntry(entries) {
        const validEntries = entries.filter((entry) => !!entry.rawId && !!entry.normalizedId);
        if (validEntries.length === 0) {
            return null;
        }
        const countByNormalizedId = new Map();
        for (const entry of validEntries) {
            countByNormalizedId.set(entry.normalizedId, (countByNormalizedId.get(entry.normalizedId) || 0) + 1);
        }
        const sortedCandidateIds = [...countByNormalizedId.entries()]
            .sort((a, b) => {
            // Prefer the ID that appears most often; tie-break lexicographically for determinism.
            if (b[1] !== a[1])
                return b[1] - a[1];
            return a[0].localeCompare(b[0]);
        })
            .map(([id]) => id);
        const canonicalId = sortedCandidateIds[0];
        const canonicalCandidates = validEntries
            .filter((entry) => entry.normalizedId === canonicalId)
            .sort((a, b) => a.partId.localeCompare(b.partId));
        return canonicalCandidates[0] || null;
    }
    async cleanupDuplicateParts(context, entries, canonicalNormalizedId, canonicalPartId) {
        const partsToDelete = entries.filter((entry) => {
            const isCanonical = entry.normalizedId === canonicalNormalizedId && entry.partId === canonicalPartId;
            return !isCanonical;
        });
        if (partsToDelete.length === 0) {
            return;
        }
        for (const entry of partsToDelete) {
            try {
                entry.part.delete();
                await context.sync();
            }
            catch (error) {
                console.warn(`[DocumentIdManager] Could not delete duplicate custom XML part ${entry.partId}:`, error);
            }
        }
    }
    buildXml(rawId) {
        return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<${this.options.rootTag} xmlns="${this.options.namespace}">
  <${this.options.idTag}>${rawId}</${this.options.idTag}>
</${this.options.rootTag}>`;
    }
}
